import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { OverviewSectionCard } from '@/components/agent/overview/OverviewSectionCard';
import { OverviewSettingRow } from '@/components/agent/overview/OverviewSettingRow';
import { TouchpointConfig, TOUCHPOINT_OPTIONS, TouchpointType } from '@/types/activation/activation-pack';

interface PackMaterialsSectionProps {
  touchpoints: TouchpointConfig;
  isEditing?: boolean;
  onEditToggle?: () => void;
  onTouchpointToggle: (key: TouchpointType) => void;
  brand?: string;
  disabled?: boolean;
}

export const PackMaterialsSection = ({
  touchpoints,
  isEditing = false,
  onEditToggle,
  onTouchpointToggle,
  brand = '#4E9CFF',
  disabled = false,
}: PackMaterialsSectionProps) => {
  const physicalTouchpoints = TOUCHPOINT_OPTIONS.filter((t) => t.category === 'physical');
  const digitalTouchpoints = TOUCHPOINT_OPTIONS.filter((t) => t.category === 'digital');
  const enabledPhysical = physicalTouchpoints.filter((t) => touchpoints[t.value]);
  const enabledDigital = digitalTouchpoints.filter((t) => touchpoints[t.value]);

  return (
    <OverviewSectionCard
      title="Materials"
      subtitle="Physical and digital assets to generate"
      genieIcon="lightbulb"
      brand={brand}
      onEdit={disabled ? undefined : onEditToggle}
    >
      {disabled ? (
        <div className="text-sm text-gray-500 italic py-4">
          Select a target above to configure materials
        </div>
      ) : isEditing ? (
        <div className="space-y-6">
          <div>
            <h4 className="text-sm font-medium mb-3">Physical Materials</h4>
            <div className="space-y-3">
              {physicalTouchpoints.map((option) => (
                <div
                  key={option.value}
                  className="flex items-start space-x-3 p-4 rounded-lg border border-gray-200 hover:border-primary/50 transition-colors cursor-pointer"
                  onClick={() => onTouchpointToggle(option.value)}
                >
                  <Checkbox
                    checked={touchpoints[option.value]}
                    onCheckedChange={() => onTouchpointToggle(option.value)}
                  />
                  <div>
                    <div className="font-medium">{option.label}</div>
                    <div className="text-sm text-muted-foreground">{option.description}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-sm font-medium mb-3">Digital Assets</h4>
            <div className="space-y-3">
              {digitalTouchpoints.map((option) => (
                <div
                  key={option.value}
                  className="flex items-start space-x-3 p-4 rounded-lg border border-gray-200 hover:border-primary/50 transition-colors cursor-pointer"
                  onClick={() => onTouchpointToggle(option.value)}
                >
                  <Checkbox
                    checked={touchpoints[option.value]}
                    onCheckedChange={() => onTouchpointToggle(option.value)}
                  />
                  <div>
                    <div className="font-medium">{option.label}</div>
                    <div className="text-sm text-muted-foreground">{option.description}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <OverviewSettingRow
            label="Physical Materials"
            value={
              enabledPhysical.length > 0 ? (
                <div className="flex flex-wrap gap-2 justify-end">
                  {enabledPhysical.map((t) => (
                    <Badge key={t.value} variant="secondary">
                      {t.label}
                    </Badge>
                  ))}
                </div>
              ) : (
                <span className="text-gray-500 italic">None selected</span>
              )
            }
          />
          <OverviewSettingRow
            label="Digital Assets"
            value={
              enabledDigital.length > 0 ? (
                <div className="flex flex-wrap gap-2 justify-end">
                  {enabledDigital.map((t) => (
                    <Badge key={t.value} variant="secondary">
                      {t.label}
                    </Badge>
                  ))}
                </div>
              ) : (
                <span className="text-gray-500 italic">None selected</span>
              )
            }
          />
        </div>
      )}
    </OverviewSectionCard>
  );
};
